import { app } from '../app';
import type { Game, ToastKind } from '../core/Game';
import type { ObjectKind } from '../world/rooms';
import { screenVectorToStep } from '../world/Grid';
import { Hud } from './Hud';
import { PanelManager, type PanelSide } from './Panel';
import { openBessiePanel, openDeskPanel, openEndDayPanel, openInventoryPanel, openSellPanel, openTechPanel, openVatPanel } from './panels';
import { openCellarPanel, openTablePanel, openTubPanel } from './stationPanels';
import { openCodexPanel } from './codexPanel';
import { openMasteryPanel } from './masteryPanel';
import { openStaffPanel } from './staffPanel';
import { SkillTree } from './skillTree';
import { Rewards } from './Rewards';

const MOVE_KEYS: Record<string, { sx: number; sy: number }> = {
  KeyW: { sx: 0, sy: -1 },
  ArrowUp: { sx: 0, sy: -1 },
  KeyS: { sx: 0, sy: 1 },
  ArrowDown: { sx: 0, sy: 1 },
  KeyD: { sx: 1, sy: 0 },
  ArrowRight: { sx: 1, sy: 0 },
  KeyA: { sx: -1, sy: 0 },
  ArrowLeft: { sx: -1, sy: 0 },
};

export class UI {
  panels: PanelManager;
  hud: Hud;
  skillTree: SkillTree;
  rewards: Rewards;
  private toasts = document.getElementById('toasts') as HTMLElement;
  private fadeEl = document.getElementById('fade') as HTMLElement;
  private held = new Set<string>();

  constructor(public game: Game) {
    this.skillTree = new SkillTree(game);
    this.panels = new PanelManager((kind) => {
      this.held.clear();
      game.focus = kind === 'task';
      game.paused = kind === 'pause';
      if (kind !== 'pause') game.abandonQuiz();
      this.hud.update();
    });
    this.hud = new Hud(game, this);
    this.rewards = new Rewards(game);
    let consumePointer = false;
    window.addEventListener('pointerdown', (event) => {
      consumePointer = this.panels.dismissOutside(event);
      if (consumePointer) this.held.clear();
    }, true);
    window.addEventListener('click', (event) => {
      if (consumePointer) { event.preventDefault(); event.stopImmediatePropagation(); consumePointer = false; }
    }, true);
    document.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement)?.closest<HTMLButtonElement>('button');
      if (!button || button.disabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      button.classList.remove('book-click');
      void button.offsetWidth;
      button.classList.add('book-click');
    }, true);
    document.addEventListener('animationend', (event) => {
      if ((event as AnimationEvent).animationName === 'book-click') (event.target as HTMLElement).classList.remove('book-click');
    });
    game.on('change', () => {
      this.hud.update();
      this.panels.update();
      this.skillTree.update();
    });
    game.on('toast', (t) => this.toast(t.text, t.kind));
    game.on('batchStage', () => {
      if (this.panels.openId === 'vat' || this.panels.openId === 'table' || this.panels.openId === 'tub' || this.panels.openId === 'cellar') this.panels.rerender();
    });
    game.on('day', () => {
      this.closePanel();
      const interrupted = Object.keys(game.state.interactiveAttempts).some(k => k.startsWith('milk:'));
      game.state.interactiveAttempts = {};
      if (interrupted) game.toast('Yesterday’s unfinished milking ended. Feed Bessie for a fresh day.', 'info');
      game.save();
    });
    window.addEventListener('keydown', (e) => this.onKey(e));
    window.addEventListener('keyup', (e) => this.held.delete(e.code));
    window.addEventListener('blur', () => this.held.clear());
    this.hud.update();
  }

  side(): PanelSide {
    const width = app.scene?.scale.width ?? window.innerWidth;
    const x = app.scene?.playerScreenX() ?? width / 2;
    return x < width / 2 ? 'right' : 'left';
  }

  heldVector(): {x:number;y:number} | null {
    if(this.playerInputBlocked)return null;
    let x=0,y=0;for(const code of this.held){const v=MOVE_KEYS[code];if(v){x+=v.sx;y+=v.sy;}}
    const d=Math.hypot(x,y);return d?{x:x/d,y:y/d}:null;
  }

  /** Compatibility helper for older callers. */
  heldStep(): { dx: number; dy: number } | null {
    if (this.playerInputBlocked) return null;
    let sx = 0;
    let sy = 0;
    for (const code of this.held) {
      const v = MOVE_KEYS[code];
      if (v) {
        sx += v.sx;
        sy += v.sy;
      }
    }
    if (sx === 0 && sy === 0) return null;
    const step = screenVectorToStep(Math.sign(sx), Math.sign(sy));
    return step.dx === 0 && step.dy === 0 ? null : step;
  }

  get playerInputBlocked(): boolean {
    return this.skillTree.isOpen || this.panels.kind === 'task' || this.panels.kind === 'pause';
  }

  openObject(kind: ObjectKind): void {
    if (this.skillTree.isOpen) return;
    switch (kind) {
      case 'bessie':
      case 'trough':
        openBessiePanel(this);
        break;
      case 'vat':
        openVatPanel(this);
        break;
      case 'table':
        openTablePanel(this);
        break;
      case 'tub':
        openTubPanel(this);
        break;
      case 'rack':
      case 'dials':
        openCellarPanel(this);
        break;
      case 'board':
        openSellPanel(this);
        break;
      case 'desk':
        openDeskPanel(this);
        break;
      case 'terminal':
        openTechPanel(this);
        break;
      default:
        break;
    }
  }

  private toggle(id: string, open: () => void): void {
    this.skillTree.close();
    if (this.panels.openId === id) this.panels.close();
    else open();
  }

  openInventory(): void {
    this.toggle('inventory', () => openInventoryPanel(this));
  }
  openSkills(): void {
    if (this.skillTree.isOpen) this.skillTree.close();
    else {
      this.panels.close();
      this.held.clear();
      this.skillTree.open();
    }
  }
  openResearch(): void {
    this.panels.close();
    this.held.clear();
    this.skillTree.openResearch();
  }
  openSupplies(): void {
    this.toggle('desk', () => openDeskPanel(this));
  }
  openCodex(): void {
    this.toggle('codex', () => openCodexPanel(this));
  }
  openMastery(): void {
    this.toggle('mastery', () => openMasteryPanel(this));
  }
  openStaff(): void {
    this.toggle('staff', () => openStaffPanel(this));
  }
  openEndDay(): void {
    if (!this.game.canEndDay) {
      this.game.toast('The work day ends at 14:00.', 'info');
      return;
    }
    this.toggle('endday', () => openEndDayPanel(this));
  }

  closePanel(): void {
    this.held.clear();
    this.skillTree.close();
    this.panels.close();
  }

  toast(text: string, kind: ToastKind = 'info'): void {
    const el = document.createElement('div');
    el.className = `toast ${kind}`;
    el.textContent = text;
    this.toasts.appendChild(el);
    while (this.toasts.children.length > 4) this.toasts.firstElementChild?.remove();
    window.setTimeout(() => el.classList.add('fade'), 2600);
    window.setTimeout(() => el.remove(), 3100);
  }

  fade(mid: () => void, after: () => void): void {
    this.fadeEl.classList.add('on');
    window.setTimeout(() => {
      mid();
      this.fadeEl.classList.remove('on');
      window.setTimeout(after, 170);
    }, 170);
  }

  private onKey(e: KeyboardEvent): void {
    const target=e.target as HTMLElement|null;
    const typing=target&&(target.tagName==='TEXTAREA'||target.isContentEditable||
      target.tagName==='INPUT'&&(e.code!=='KeyE'||['text','search','email','url','tel','password','number'].includes((target as HTMLInputElement).type))||
      target.tagName==='SELECT'&&e.code!=='KeyE');
    if(typing)return;
    if(e.code==='KeyE'&&e.repeat){e.preventDefault();return;}
    // The tree owns keyboard/controller navigation and keeps room movement frozen.
    if (this.skillTree.isOpen) {
      this.held.clear();
      if (e.code === 'Escape' || e.code === 'KeyK' || e.code === 'KeyE') { e.preventDefault(); this.skillTree.back(); }
      return;
    }
    if(e.code==='KeyE'&&this.panels.openId){e.preventDefault();this.closePanel();return;}
    if (e.code === 'Escape') {
      this.panels.close();
      return;
    }
    // Minigames own Space and the task panel owns the player's attention
    if (this.panels.kind === 'task' && e.code === 'Space') return;
    if (MOVE_KEYS[e.code]) {
      e.preventDefault();
      if (this.panels.kind === 'pause') return;
      this.held.add(e.code);
      if (e.repeat) return;
      app.scene?.cancelRoute();
      return;
    }
    switch (e.code) {
      case 'KeyI':
        this.openInventory();
        return;
      case 'KeyK':
        this.openSkills();
        return;
      case 'KeyB':
        this.openSupplies();
        return;
      case 'KeyC':
        this.openCodex();
        return;
      case 'KeyM':
        this.openMastery();
        return;
      case 'KeyT':
        this.openStaff();
        return;
      case 'KeyN':
        this.openEndDay();
        return;
      case 'KeyE':
      case 'Space': {
        e.preventDefault();
        const obj = app.scene?.nearbyObject();
        if (obj) app.scene?.interact(obj);
        return;
      }
      default:
        return;
    }
  }
}
